/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  ApolloClient,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client'
import { type ApolloCache, NetworkStatus } from '@apollo/client'
import type { SkipToken } from '@apollo/client/react'
import { skipToken, useMutation, useQuery } from '@apollo/client/react'
import { uniqueId } from 'lodash-es'
import { useMemo } from 'react'
import type { Connection } from 'src/utils/graphql'
import type { ApolloClientInitialContext } from './client'
import { baseApolloClient } from './client'
import type { PageInfo } from './generated/graphql'

const mergeContext = (
  base?: ApolloClientInitialContext,
  override?: ApolloClientInitialContext,
): ApolloClientInitialContext => {
  return {
    ...base,
    ...override,
    fetchOptions: { ...base?.fetchOptions, ...override?.fetchOptions },
    headers: { ...base?.headers, ...override?.headers },
  }
}

// v4: Generic override options for better type safety
type UseQueryOverrideOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
> = Omit<useQuery.Options<TData, TVariables>, 'variables'> & {
  context?: ApolloClientInitialContext
}

// v4: Hook interface with call signature
// Define the result shape inline to avoid circular dependencies
interface UseQueryHook<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
> {
  (
    variables: TVariables | SkipToken,
    overrideOptions?: UseQueryOverrideOptions<TData, TVariables>,
  ): ReturnType<typeof useQuery<TData, TVariables>>
}

export const createQueryHook = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  document: TypedDocumentNode<TData, TVariables>,
  options?: UseQueryOverrideOptions<TData, TVariables>,
): UseQueryHook<TData, TVariables> => {
  return (
    variables: TVariables | SkipToken,
    overrideOptions?: UseQueryOverrideOptions<TData, TVariables>,
  ): ReturnType<UseQueryHook<TData, TVariables>> => {
    // v4: Pass skipToken directly as second argument, or pass options with variables
    const queryOptions =
      variables === skipToken
        ? skipToken
        : ({
            ...options,
            ...overrideOptions,
            context: mergeContext(options?.context, overrideOptions?.context),
            variables,
          } as useQuery.Options<TData, TVariables>)

    return useQuery(document, queryOptions) as unknown as ReturnType<
      UseQueryHook<TData, TVariables>
    >
  }
}

// Extract data type from hook return type
type DataFromHook<THook> = THook extends (...args: any[]) => infer TResult
  ? TResult extends { data: infer TData }
    ? TData
    : unknown
  : unknown

// Extract variables type from hook's first parameter
type VariablesFromHook<THook> = THook extends (
  variables: infer TVariables,
  ...args: any[]
) => any
  ? Exclude<TVariables, SkipToken>
  : unknown

// Generic hook type for chaining
type AnyQueryHook = (...args: any[]) => ReturnType<UseQueryHook<any, any>>

// Simplified modifyQueryHook with explicit output type parameter
export function modifyQueryHook<
  THook extends AnyQueryHook,
  TOutput = DataFromHook<THook>,
  TInput = VariablesFromHook<THook>,
>(
  useQueryHook: THook,
  modifier: {
    transform?: (param: TInput) => VariablesFromHook<THook> | SkipToken
    reduce?: (param: DataFromHook<THook> | undefined) => TOutput
  },
) {
  const { transform, reduce } = modifier

  return (
    input: TInput | SkipToken,
    options?: UseQueryOverrideOptions<TOutput, any>,
  ): ReturnType<UseQueryHook<TOutput, any>> => {
    const { data, previousData, ...rest } = useQueryHook(
      input === skipToken
        ? skipToken
        : transform !== undefined
          ? transform(input)
          : input,
      options,
    )
    return {
      data: (data === undefined
        ? data
        : reduce !== undefined
          ? reduce(data)
          : data) as TOutput,
      previousData: (previousData === undefined
        ? previousData
        : reduce !== undefined
          ? reduce(previousData)
          : previousData) as TOutput | undefined,
      ...rest,
    } as ReturnType<UseQueryHook<TOutput, any>>
  }
}

const extractPagedData = (
  data: Required<Connection<any>> & {
    pageInfo: PageInfo
  },
  variables:
    | {
        before?: string
        after?: string
        first?: number
        last?: number
      }
    | typeof skipToken,
  loading: boolean,
) => {
  if (variables === skipToken || data == null) {
    return undefined
  }
  // see https://relay.dev/graphql/connections.htm for the algorithms
  const { edges, pageInfo, ...extras } = data
  const { before, after, first, last } = variables
  // ApplyCursorsToEdges
  let startIndex = 0
  let endIndex = edges.length
  if (after) {
    const afterEdgeIndex = edges.findIndex(edge => edge?.cursor === after)
    if (afterEdgeIndex > 0) {
      startIndex = Math.max(startIndex, afterEdgeIndex + 1)
    } else if (loading) {
      // avoid inconsistent when loading
      return undefined
    }
  }
  if (before) {
    const beforeEdgeIndex = edges.findLastIndex(edge => edge?.cursor === before)
    if (beforeEdgeIndex > 0) {
      endIndex = Math.min(endIndex, beforeEdgeIndex)
    } else if (loading) {
      // avoid inconsistent when loading
      return undefined
    }
  }
  // EdgesToReturn
  if (first && first >= 0) {
    endIndex = Math.min(endIndex, startIndex + first)
  }
  if (last && last >= 0) {
    startIndex = Math.max(startIndex, endIndex - last)
  }
  const edgesToReturn = edges.slice(startIndex, endIndex)
  const pageInfoToReturn = {
    hasNextPage: endIndex < edges.length || pageInfo.hasNextPage,
    hasPreviousPage: startIndex > 0 || pageInfo.hasPreviousPage,
    startCursor: edgesToReturn.at(0)?.cursor,
    endCursor: edgesToReturn.at(-1)?.cursor,
  } satisfies PageInfo
  return {
    ...extras,
    edges: edgesToReturn,
    pageInfo: pageInfoToReturn,
  }
}

/**
 * In Apollo Client, when a field is using relayStylePagination in its typePolicies,
 * the query hook will returns everything in cache and ignore pagination args(before, after, first, last).
 * It expects the user of the data to extract individual pages.
 * This helper will return the hook that return the requested page instead of full data
 * Note: you need to use modifyQueryHook to extract the list from data before hand.
 */
export const pagedQueryHook = <THook extends AnyQueryHook>(
  useQueryHook: THook,
) => {
  return (
    variables: VariablesFromHook<THook> | SkipToken,
    overrideOptions?: UseQueryOverrideOptions<DataFromHook<THook>, any>,
  ): ReturnType<UseQueryHook<DataFromHook<THook>, any>> => {
    const { data, previousData, ...rest } = useQueryHook(
      variables,
      overrideOptions,
    )
    const pagedData = useMemo(() => {
      return extractPagedData(data, variables, rest.loading)
    }, [data, variables, rest.loading])
    const pagedPreviousData = useMemo(() => {
      return extractPagedData(previousData, variables, rest.loading)
    }, [previousData, variables, rest.loading])
    return {
      data: pagedData as DataFromHook<THook>,
      previousData: pagedPreviousData as DataFromHook<THook> | undefined,
      ...rest,
    }
  }
}

/**
 * In Apollo Client, refetch will reset cache content of infinity query, only keeps first page
 * This helper will disable automatic refetch for those query hooks.
 * This helper provide an additional loadingMore state that indicates is fetchMore running
 */
export const infiniteQueryHook = <THook extends AnyQueryHook>(
  useQueryHook: THook,
) => {
  return (
    variables: VariablesFromHook<THook> | SkipToken,
    overrideOptions?: UseQueryOverrideOptions<DataFromHook<THook>, any>,
  ): ReturnType<UseQueryHook<DataFromHook<THook>, any>> & {
    loadingMore: boolean
  } => {
    const result = useQueryHook(variables, {
      ...overrideOptions,
      context: mergeContext(
        {
          refetchOnFocus: false,
          refetchOnReconnect: false,
        },
        overrideOptions?.context,
      ),
    })
    const loadingMore = result.networkStatus === NetworkStatus.fetchMore
    return { ...result, loadingMore }
  }
}

type QueryOverrideOptions<
  TVariables extends OperationVariables = OperationVariables,
  TData = any,
> = Omit<
  Parameters<typeof baseApolloClient.query<TData, TVariables>>[0],
  'variables' | 'query'
>

export const createFetchQueryHelper = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  document: TypedDocumentNode<TData, TVariables>,
  options?: QueryOverrideOptions<TVariables, TData>,
) => {
  return (
    variables: TVariables,
    overrideOptions?: QueryOverrideOptions<TVariables, TData>,
  ) =>
    baseApolloClient.query({
      ...options,
      ...overrideOptions,
      context: mergeContext(
        options?.context as ApolloClientInitialContext,
        overrideOptions?.context as ApolloClientInitialContext,
      ),
      query: document,
      variables,
    })
}

export const createRefetchQueryHelper = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  document: TypedDocumentNode<TData, TVariables>,
) => {
  return () => {
    baseApolloClient.refetchQueries({
      include: [document],
    })
  }
}

// v4: Use any for context to avoid DefaultContext vs ApolloClientInitialContext issues
// v4: ApolloCache is no longer generic
type MutationSharedOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
  _TContext = any,
  TCache extends ApolloCache = ApolloCache,
> = Omit<
  Parameters<typeof baseApolloClient.mutate<TData, TVariables, TCache>>[0],
  'mutation' | 'variables'
> & {
  context?: ApolloClientInitialContext
}

export type MutateResult<TData> = {
  data?: TData | null
  errors?: readonly any[]
}

/**
 * @returns A tuple. The first element is the mutation hooks, the second element is a function used to execute the mutation
 */
export const createMutationHelper = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  document: TypedDocumentNode<TData, TVariables>,
  options?: MutationSharedOptions<TData, TVariables>,
) => {
  /**
   * Imperative mutation runner.
   *
   * The third argument lets the caller pin the mutation to a specific
   * Apollo client instance. Pass the route-scoped client returned by
   * `useApolloClient()` whenever the surface that triggered the
   * mutation reads its UI from a route-scoped `<ApolloProvider>`.
   *
   * Why this matters: every `mutate()` call also dispatches the
   * registered `update(cache, ...)` callback against the *receiving
   * client's* cache. The Stage 2 SSR rollout introduced
   * `createRouteApolloClient` (see `route-ssr-client.ts`) so detail
   * pages own a per-route Apollo cache seeded with the SSR-prefetched
   * payload. If we keep defaulting to `baseApolloClient` here, a
   * mutation triggered from inside a `<ApolloProvider client={routeClient}>`
   * subtree will:
   *   1. write the response into `baseApolloClient.cache`
   *   2. run `update(cache, ...)` against `baseApolloClient.cache`
   *   3. NOT touch `routeClient.cache`
   * → every `useQuery`/`useSubscription` consumer in the route subtree
   * (which subscribes to `routeClient.cache`) keeps showing stale
   * data until the user reloads the page. The artwork-detail comment
   * box shipping a comment that doesn't appear until refresh
   * (2026-05-14) is exactly this regression.
   *
   * Falling back to `baseApolloClient` keeps every CSR-only call site
   * (the vast majority) working unchanged — the global `<RootRuntime>`
   * `<ApolloProvider>` already wires `baseApolloClient`, so there's no
   * cache split to repair there.
   */
  const executeMutation = (
    variables: TVariables,
    overrideOptions?: MutationSharedOptions<TData, TVariables>,
    apolloClient: ApolloClient = baseApolloClient,
  ): Promise<MutateResult<TData>> =>
    apolloClient.mutate({
      ...options,
      ...overrideOptions,
      variables,
      mutation: document,
    }) as Promise<MutateResult<TData>>

  const useMutationHook = (
    overrideOptions?: useMutation.Options<TData, TVariables>,
  ) =>
    useMutation<TData, TVariables>(document, {
      ...options,
      ...overrideOptions,
      context: mergeContext(
        options?.context,
        overrideOptions?.context as ApolloClientInitialContext,
      ),
    })
  return [useMutationHook, executeMutation] as const
}

// The is used to keep cursor in infinity query result after
// manually insertion of entries to the local cache consistent.
export const createLocalCursor = () => uniqueId('!local-cursor')
